import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import { getPropertyTenantStatusLabel } from '../../TenantComponent/ScreeningsComponent/screeningEnums';
import {
  getPropertyListingStatusLabel,
  getPropertyListingStatusSlug,
} from '../../constants/propertyListingStatus';

const propertyTypeMap = {
  '1': 'Apartment',
  '2': 'House',
  '3': 'Townhouse',
  '4': 'Studio',
};

const formatScreeningDate = (createdAt) => {
  if (createdAt == null) return '—';
  const d = new Date(typeof createdAt === 'number' ? createdAt : Number(createdAt));
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

const transformPropertyItem = (item) => {
  const prop = item.property || item.Property || item;

  const addressPart = prop.address || '';
  const unitPart = prop.unit_number ? `, ${prop.unit_number}` : '';
  const cityState = [prop.city, prop.state].filter(Boolean).join(', ');

  const statusRaw = prop.status ?? item.status;
  const status = getPropertyListingStatusLabel(statusRaw);
  const statusSlug = getPropertyListingStatusSlug(statusRaw);

  return {
    id: prop.id ?? prop.property_id ?? item.id,
    propertyType: propertyTypeMap[String(prop.property_type)] || prop.property_type || '—',
    propertyCode: prop.property_code ?? prop.code ?? '—',
    propertyAddress: [addressPart + unitPart, cityState].filter(Boolean).join(', ').trim() || '—',
    status,
    statusSlug,
  };
};

const transformScreeningItem = (item) => {
  const landlord = item.landlord_screening_landlord_calls || {};
  const landlordName = [landlord.first_name, landlord.last_name].filter(Boolean).join(' ') || '—';
  const prop = item.property || item.Property || {};
  const propertyType = propertyTypeMap[String(prop.property_type)] || prop.property_type || '—';
  const propertyCode = prop.property_code ?? '—';
  const addressPart = prop.address || '';
  const unitPart = prop.unit_number ? `, ${prop.unit_number}` : '';
  const cityPart = prop.city ? ` ${prop.city}` : '';
  const propertyAddress = [addressPart + unitPart, cityPart].filter(Boolean).join(',').trim() || '—';

  const tenant = item.tenant || item.tenants?.[0];
  const tenantName = tenant
    ? [tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || '—'
    : '—';

  const status = getPropertyTenantStatusLabel(
    item.property_tenant_status ?? item.property?.property_tenant_status ?? item.status
  );

  const propertyId = (prop.id ?? prop.property_id ?? item.property_id) != null
    ? String(prop.id ?? prop.property_id ?? item.property_id)
    : null;

  const grade = item.grade != null && item.grade !== '' ? String(item.grade) : '—';

  return {
    id: item.id,
    propertyId,
    landlordName,
    tenantName,
    propertyType,
    propertyCode,
    propertyAddress,
    screeningDate: formatScreeningDate(item.createdAt),
    grade,
    status,
  };
};

const getStatusClass = (status) => {
  const statusLower = (status || '').toLowerCase();
  if (statusLower === 'verified') return 'screenings-status-verified';
  if (statusLower === 'rejected') return 'screenings-status-rejected';
  if (statusLower === 'available') return 'screenings-status-verified';
  if (statusLower === 'rented') return 'screenings-status-verified';
  if (statusLower === 'completed') return 'screenings-status-rejected';
  if (statusLower === 'unavailable') return 'screenings-status-rejected';
  if (statusLower === 'pending') return 'screenings-status-pending';
  return 'screenings-status-pending';
};

// Grade badge colors: 1 = light green, 2 = light blue, 3 = light red, other = neutral
const getGradeStyle = (grade) => {
  const g = grade != null && grade !== '' ? String(grade).trim() : '';
  if (g === '1') return { backgroundColor: '#d4edda', color: '#155724' };
  if (g === '2') return { backgroundColor: '#ABF0FD', color: '#004085' };
  if (g === '3') return { backgroundColor: '#f8d7da', color: '#721c24' };
  return { backgroundColor: '#e9ecef', color: '#495057' };
};

const DashboardItems = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const typeParam = searchParams.get('type');
  const numericType = typeParam != null ? Number(typeParam) : null;

  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const title = useMemo(() => {
    if (numericType === 0) return 'Active Properties';
    if (numericType === 1) return 'Expired Properties';
    if (numericType === 2) return 'Total Screening';
    return 'Total Properties';
  }, [numericType]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: '1',
          limit: '10',
        });

        if (numericType === 0 || numericType === 1 || numericType === 2) {
          params.set('type', String(numericType));
        }

        const response = await getApi(`/dashboard/items?${params.toString()}`);

        if (response?.statusCode === 200 && response?.data) {
          const data = response.data;
          // API shape:
          // {
          //   dashboard_type: 'total_properties' | 'active_properties' | 'expired_properties' | 'screenings',
          //   totalItems,
          //   totalPages,
          //   currentPage,
          //   pageSize,
          //   data: [...]
          // }
          const rawItems = Array.isArray(data.data) ? data.data : [];

          if (numericType === 2) {
            setItems(rawItems.map(transformScreeningItem));
          } else {
            setItems(rawItems.map(transformPropertyItem));
          }
        } else {
          setItems([]);
        }
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || 'Failed to load items.';
        setError(message);
        setItems([]);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [numericType]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase();

    if (!query) return items;

    if (numericType === 2) {
      return items.filter((item) =>
        item.landlordName.toLowerCase().includes(query) ||
        item.propertyType.toLowerCase().includes(query) ||
        item.propertyCode.toLowerCase().includes(query) ||
        item.propertyAddress.toLowerCase().includes(query)
      );
    }

    return items.filter((item) =>
      item.propertyType.toLowerCase().includes(query) ||
      item.propertyCode.toLowerCase().includes(query) ||
      item.propertyAddress.toLowerCase().includes(query) ||
      (item.status || '').toLowerCase().includes(query)
    );
  }, [items, searchQuery, numericType]);

  const handleViewProperty = (item) => {
    // For screening view, use id from the property object (propertyId); for property lists use item.id
    const id = numericType === 2 ? (item.propertyId || item.id) : (item.id || item.propertyId);
    if (id) {
      navigate(`/landlord/properties/${id}`);
    } else {
      toast.info('Property details are not available for this item.');
    }
  };

  if (loading) {
    return (
      <div className="screenings-page">
        <div className="screenings-actions-bar">
          <div className="screenings-search-wrapper">
            <img src="/assets/images/SearchIcon.svg" alt="" className="screenings-search-icon" aria-hidden />
            <input
              type="text"
              className="screenings-search-input"
              placeholder="Search"
              value=""
              readOnly
              disabled
              aria-label="Search (available when loaded)"
            />
          </div>
        </div>
        <h2 className="screenings-title">{title}</h2>
        <div className="screenings-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '18px', color: '#666' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="screenings-page">
        <div className="screenings-content">
          <p style={{ color: 'var(--color-error, #dc3545)' }}>{error}</p>
        </div>
      </div>
    );
  }

  const isScreeningView = numericType === 2;

  return (
    <div className="screenings-page">
      <div className="screenings-actions-bar">
        <button
          type="button"
          className="screenings-back-button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <span className="screenings-back-button-icon" aria-hidden="true">
            ←
          </span>
        </button>
        <div className="screenings-search-wrapper">
          <img
            src="/assets/images/SearchIcon.svg"
            alt="Search"
            className="screenings-search-icon"
          />
          <input
            type="text"
            className="screenings-search-input"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <h2 className="screenings-title">
        {title} ({filteredItems.length})
      </h2>

      <div className="screenings-content">
        {filteredItems.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '18px', color: '#666' }}>
            No data found.
          </div>
        ) : (
          <table className="screenings-table">
            <thead>
              <tr>
                <th>Sr. no.</th>
                {isScreeningView ? (
                  <>
                    <th>Property Type</th>
                    <th>Property Code</th>
                    <th>Property Address</th>
                    <th>Tenant Name</th>
                    <th>Screening Date</th>
                    <th>Grade</th>
                    <th>Status</th>
                    <th>Action</th>
                  </>
                ) : (
                  <>
                    <th>Property Type</th>
                    <th>Property Code</th>
                    <th>Property Address</th>
                    <th>Status</th>
                    <th>Action</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <tr key={item.id ?? index}>
                  <td>{index + 1}</td>
                  {isScreeningView ? (
                    <>
                      <td>{item.propertyType}</td>
                      <td>{item.propertyCode}</td>
                      <td className="screenings-cell-address" title={item.propertyAddress}>
                        <span className="screenings-cell-address-text">{item.propertyAddress}</span>
                      </td>
                      <td>{item.tenantName}</td>
                      <td>{item.screeningDate}</td>
                      <td>
                        <span className="screenings-grade-pill" style={getGradeStyle(item.grade)}>
                          {item.grade}
                        </span>
                      </td>
                      <td>
                        <span className={`screenings-status-pill ${getStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="screenings-view-detail-button"
                          onClick={() => handleViewProperty(item)}
                        >
                          View detail
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{item.propertyType}</td>
                      <td>{item.propertyCode}</td>
                      <td className="screenings-cell-address" title={item.propertyAddress}>
                        <span className="screenings-cell-address-text">{item.propertyAddress}</span>
                      </td>
                      <td>
                        <span className={`screenings-status-pill ${getStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="screenings-view-detail-button"
                          onClick={() => handleViewProperty(item)}
                        >
                          View detail
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DashboardItems;

